import React, { useEffect, useState, useMemo } from "react";
import { Button, Card, Descriptions, Select } from "antd";
import { useInputs } from "../../../hooks/useInputs";
import { MAIN_BAND_ID } from "../../../constants";
import { useResourceContext } from "../../common/resource/useResourceContext";
import { ScootStatus } from "../service/scooter.utils";
import { useAuthContext } from "../../auth/context/useAuthContext";
import { FilterSelect, FilterInput, createBinaryOptions, createOptions } from "./FilterComponents";

const { Option } = Select;

const ScootsTableFilter = ({
  bands,
  taskDefinitions,
  capital,
  geofence,
  setFilterOptions,
}) => {

  const {auth} = useAuthContext();

  const [qrInput, handleQrInput, handleQrInputReset] = useInputs("", "input");
  const [imeiInput, handleImeiInput, handleImeiInputReset] = useInputs("", "input");
  const [batteryInput, handleBatteryInput, handleBatteryInputReset] = useInputs("", "input");
  
  const [allocateDateInput, handleAllocateDateInput, handleAllocateDateInputReset] = useInputs("", "input");
  const [lastRideDateInput, handleLastRideDateInput, handleLastRideDateInputReset] = useInputs("", "input");
  const [gpsUpdatedAtInput, handleGpsUpdatedAtInput, handleGpsUpdatedAtInputReset] = useInputs("", "input");
  const [dataUpdatedAtInput, handleDataUpdatedAtInput, handleDataUpdatedAtInputReset] = useInputs("", "input");
  const [lastWorkAtInput, handleLastWorkAtInput, handleLastWorkAtInputReset] = useInputs("", "input");
  
  const [metaSelect, handleMetaSelect, handleMetaSelectReset] = useInputs([], "select");
  const [bandSelect, handleBandSelect, handleBandSelectReset] = useInputs(
    auth.band.id === MAIN_BAND_ID ? [auth.band.id] : [],
    "select"
  );
  const [capitalSelect, handleCapitalSelect, handleCapitalSelectReset] = useInputs([], "select");
  const [geofenceSelect, handleGeofenceSelect, handleGeofenceSelectReset] = useInputs([], "select");
  const [statusSelect, handleStatusSelect, handleStatusSelectReset] = useInputs(
    auth.band.id === MAIN_BAND_ID ? ["Ready"] : [],
    "select"
  );
  const [vehicleTaskTypeSelect, handleVehicleTaskTypeSelect, handleVehicleTaskTypeSelectReset] = useInputs([], "select");
  
  const [isRidingSelect, handleIsRidingSelect, handleIsRidingSelectReset] = useInputs(null, "select");
  const [brokenSelect, handleBrokenSelect, handleBrokenSelectReset] = useInputs(null, "select");
  const [availableSelect, handleAvailableSelect, handleAvailableSelectReset] = useInputs(null, "select");
  const [isLockedSelect, handleIsLockedSelect, handleIsLockedSelectReset] = useInputs(null, "select");
  const [iotBrokenSelect, handleIotBrokenSelect, handleIotBrokenSelectReset] = useInputs(null, "select");
  const [fromJapanSelect, handleFromJapanSelect, handleFromJapanSelectReset] = useInputs(null, "select");
  const [backupModeSelect, handleBackupModeSelect, handleBackupModeSelectReset] = useInputs(null, "select");
  const [inspectSelect, handleInspectSelect, handleInspectSelectReset] = useInputs(null, "select");
  
  const [collectFilter, setCollectFilter] = useState(false);
  const [disuseFilter, setDisuseFilter] = useState(false);
  const { R } = useResourceContext();

  // 리셋 함수 그룹화
  const resetFunctions = [
    handleQrInputReset,
    handleImeiInputReset,
    handleBatteryInputReset,
    handleAllocateDateInputReset,
    handleLastRideDateInputReset,
    handleGpsUpdatedAtInputReset,
    handleDataUpdatedAtInputReset,
    handleLastWorkAtInputReset,
    handleInspectSelectReset,
    handleGeofenceSelectReset,
    handleCapitalSelectReset,
    handleMetaSelectReset,
    handleBandSelectReset,
    handleStatusSelectReset,
    handleVehicleTaskTypeSelectReset,
    handleAvailableSelectReset,
    handleBrokenSelectReset,
    handleIsRidingSelectReset,
    handleIsLockedSelectReset,
    handleIotBrokenSelectReset,
    handleFromJapanSelectReset,
    handleBackupModeSelectReset
  ];

  const filterInit = () => {
    resetFunctions.forEach(resetFn => resetFn());
    setCollectFilter(false);
    setDisuseFilter(false);
  };

  const moveBandForward = (bands) => {
    return bands
      .filter((x) => x.id === MAIN_BAND_ID)
      .concat(bands.filter((x) => x.id !== MAIN_BAND_ID));
  };

  const bandOptions = useMemo(() => {
    return moveBandForward(bands).map((e, i) => (
      <Option key={"band-option-" + i} value={e.id}>
        {e.name ? e.name : "invalid band name"}
      </Option>
    ));
  }, [bands]);

  const metaOptions = useMemo(() => {
    return ["S5", "S7", "S8", "S9", "S11", "W1", "W7", "W9"].map((e, i) => (
      <Option key={"meta-option-" + i} value={e}>
        {e}
      </Option>
    ));
  }, []);

  const capitalOptions = useMemo(() => {
    return capital.map((e, i) => (
      <Option key={`${e.id}`} value={e.id}>
        {e.description ? e.description : "invalid capital name"}
      </Option>
    ));
  }, [capital]);

  const geofenceOptions = useMemo(() => {
    return createOptions(geofence, "geofence", "id", "name");
  }, [geofence]);

  const statusOptions = useMemo(() => {
    return ScootStatus.map((e, i) => (
      <Option key={"status-option-" + i} value={e.value}>
        {e.text ? e.text : "invalid status"}
      </Option>
    ));
  }, []);

  const vehicleTaskTypeOptions = useMemo(() => {
    return [...taskDefinitions, {displayName: "-", name: "NOTHING"}].map((e, i) => (
      <Option key={"vehicle-task-type-option-" + i} value={e.name}>
        {e.displayName}
      </Option>
    ));
  }, [taskDefinitions]);
  
  const binaryOptions = useMemo(() => {
    return createBinaryOptions("O", "X");
  }, []);

  const filterOptions = useMemo(() => ({
    qr: qrInput,
    imei: imeiInput,
    isRiding: isRidingSelect,
    isLocked: isLockedSelect,
    iotBroken: iotBrokenSelect,
    allocateDate: allocateDateInput,
    lastRideDate: lastRideDateInput,
    gpsUpdatedAt: gpsUpdatedAtInput,
    dataUpdatedAt: dataUpdatedAtInput,
    lastWorkAt: lastWorkAtInput,
    broken: brokenSelect,
    available: availableSelect,
    battery: batteryInput,
    inspect: inspectSelect,
    geofence: geofenceSelect,
    capital: capitalSelect,
    meta: metaSelect,
    band: bandSelect,
    status: statusSelect,
    fromJapan: fromJapanSelect,
    vehicleTaskType: vehicleTaskTypeSelect,
    backupMode: backupModeSelect,
  }), [
    qrInput,
    imeiInput,
    inspectSelect,
    geofenceSelect,
    capitalSelect,
    metaSelect,
    bandSelect,
    brokenSelect,
    availableSelect,
    statusSelect,
    isRidingSelect,
    isLockedSelect,
    iotBrokenSelect,
    fromJapanSelect,
    vehicleTaskTypeSelect,
    allocateDateInput,
    lastRideDateInput,
    gpsUpdatedAtInput,
    dataUpdatedAtInput,
    lastWorkAtInput,
    batteryInput,
    backupModeSelect
  ]);
  
  useEffect(() => {
    setFilterOptions(filterOptions);
  }, [filterOptions, setFilterOptions]);

  // 수집 및 사용 중지 필터 효과 통합
  useEffect(() => {
    // 수집 필터
    if (collectFilter) {
      handleBandSelect([localStorage.getItem("bandID")]);
      handleMetaSelect(["S7", "S8", "S9", "S11", "W1", "W7", "W9"]);
      handleStatusSelect([
        "WaitingForCapture",
        "Missing",
        "MissingThree",
        "WaitingForReallocate",
        "WaitingForChangeBattery",
      ]);
    } else if (!disuseFilter) { // 둘 다 꺼져 있을 때만 리셋
      handleBandSelectReset();
      handleMetaSelectReset();
      handleStatusSelectReset();
    }
  }, [collectFilter, disuseFilter]);

  // 사용 중지 필터 - statusSelect만 영향
  useEffect(() => {
    if (disuseFilter && !collectFilter) { // 수집 필터가 켜져 있으면 해당 필터 우선
      handleStatusSelect([
        "Cargo",
        "Ready",
        "Reallocated",
        "Captured",
        "CapturedForReallocating",
        "Missing",
        "MissingTwo",
        "MissingThree",
        "Lost",
        "Exception",
        "Report",
        "WaitingForReallocate",
        "CargoForReady",
        "WaitingForChangeBattery",
      ]);
    } else if (!collectFilter) { // collectFilter가 꺼져 있을 때만 실행
      handleStatusSelectReset();
    }
  }, [disuseFilter, collectFilter]);

  return (
        <Card title={R.text.e_scooters_e_bikes_filter}>
          <Descriptions
            bordered
            size={"small"}
            labelStyle={{ width: "112px" }}
            column={{ xxl: 4, xl: 4, lg: 3, md: 2, sm: 1, xs: 1 }}
          >
            {auth.band.id !== 'clypl7wgb03zf0l78xijzhen5' && (
              <Descriptions.Item label={R.text.filter_and_action} span={4}>
              <Button
                style={{ marginRight: "4px" }}
                size={"small"}
                type={collectFilter ? "primary" : "default"}
                onClick={() => setCollectFilter(!collectFilter)}
              >
                {collectFilter
                  ? R.text.collector_filter_off
                  : R.text.collector_filter_on}
              </Button>
              <Button
                style={{ marginRight: "4px" }}
                size={"small"}
                type={disuseFilter ? "primary" : "default"}
                onClick={() => setDisuseFilter(!disuseFilter)}
              >
                {disuseFilter
                  ? R.text.disuse_filter_off
                  : R.text.disuse_filter_on}
              </Button>
              <Button
                style={{ marginRight: "4px" }}
                size={"small"}
                type={"primary"}
                ghost
                danger
                onClick={filterInit}
              >
                {R.text.initial}
              </Button>
            </Descriptions.Item>
          )}
            <Descriptions.Item label={R.text.qr}>
              <FilterInput
                value={qrInput}
                onChange={handleQrInput}
                placeholder={R.text.qr}
              />
            </Descriptions.Item>
            <Descriptions.Item label={R.text.imei}>
              <FilterInput
                value={imeiInput}
                onChange={handleImeiInput}
                placeholder={R.text.imei}
              />
            </Descriptions.Item>
            {auth.band.id !== 'clypl7wgb03zf0l78xijzhen5' ? (
              <>
                <Descriptions.Item label={R.text.device_model}>
                  <FilterSelect
                    value={metaSelect}
                    onChange={handleMetaSelect}
                    placeholder="S9/S11..."
                    options={metaOptions}
                    mode="multiple"
                  />
                </Descriptions.Item>
                <Descriptions.Item label={R.text.region_name}>
                  <FilterSelect
                    value={bandSelect}
                    onChange={handleBandSelect}
                    placeholder={R.text.region_name}
                    options={bandOptions}
                    mode="multiple"
                  />
                </Descriptions.Item>
                <Descriptions.Item label={R.text.device_owner}>
                  <FilterSelect
                    value={capitalSelect}
                    onChange={handleCapitalSelect}
                    placeholder={R.text.headquarters}
                    options={capitalOptions}
                    mode="multiple"
                  />
                </Descriptions.Item>
              </>
            ) : null}
            <Descriptions.Item label={R.text.battery}>
              <FilterInput
                value={batteryInput}
                onChange={handleBatteryInput}
                placeholder="--%"
              />
            </Descriptions.Item>
            <Descriptions.Item label={R.text.gps_location}>
              <FilterSelect
                value={geofenceSelect}
                onChange={handleGeofenceSelect}
                placeholder={`${R.text.seoul} ~~`}
                options={geofenceOptions}
                mode="multiple"
              />
            </Descriptions.Item>
            <Descriptions.Item label={R.text.ride_status}>
              <FilterSelect
                value={isRidingSelect}
                onChange={handleIsRidingSelect}
                placeholder="O/X"
                options={binaryOptions}
              />
            </Descriptions.Item>
            <Descriptions.Item label={R.text.lock_status}>
              <FilterSelect
                value={isLockedSelect}
                onChange={handleIsLockedSelect}
                placeholder={`${R.text.locked}/${R.text.unlock}`}
                options={[
                  <Option key="locked-option-o" value="O">{R.text.locked}</Option>,
                  <Option key="locked-option-x" value="X">{R.text.unlock}</Option>
                ]}
              />
            </Descriptions.Item>
            <Descriptions.Item label={R.text.iot_status}>
              <FilterSelect
                value={iotBrokenSelect}
                onChange={handleIotBrokenSelect}
                placeholder={`${R.text.offline}/${R.text.normal}`}
                options={[
                  <Option key="iot-option-o" value="O">{R.text.offline}</Option>,
                  <Option key="iot-option-x" value="X">{R.text.normal}</Option>
                ]}
              />
            </Descriptions.Item>
            <Descriptions.Item label={R.text.device_status}>
              <FilterSelect
                value={statusSelect}
                onChange={handleStatusSelect}
                placeholder={`${R.text.device_status}..`}
                options={statusOptions}
                mode="multiple"
              />
            </Descriptions.Item>
            <Descriptions.Item label={"고장 여부"}>
              <FilterSelect
                value={brokenSelect}
                onChange={handleBrokenSelect}
                placeholder="O/X"
                options={binaryOptions}
              />
            </Descriptions.Item>
            <Descriptions.Item label={"백업모드 여부"}>
              <FilterSelect
                value={backupModeSelect}
                onChange={handleBackupModeSelect}
                placeholder="O/X"
                options={binaryOptions}
              />
            </Descriptions.Item>
            <Descriptions.Item label={"이용가능여부"}>
              <FilterSelect
                value={availableSelect}
                onChange={handleAvailableSelect}
                placeholder="O/X"
                options={binaryOptions}
              />
            </Descriptions.Item>
            <Descriptions.Item label={R.text.placement_date}>
              <FilterInput
                value={allocateDateInput}
                onChange={handleAllocateDateInput}
                placeholder={R.text.placement_date}
              />
            </Descriptions.Item>
            <Descriptions.Item label={R.text.inspection_status}>
              <FilterSelect
                value={inspectSelect}
                onChange={handleInspectSelect}
                placeholder="O/X"
                options={binaryOptions}
              />
            </Descriptions.Item>
            <Descriptions.Item label={R.text.last_iot_received}>
              <FilterInput
                value={dataUpdatedAtInput}
                onChange={handleDataUpdatedAtInput}
                placeholder={R.text.last_iot_received}
              />
            </Descriptions.Item>
            <Descriptions.Item label={"마지막 작업일자"}>
              <FilterInput
                value={lastWorkAtInput}
                onChange={handleLastWorkAtInput}
                placeholder={"마지막 작업일자"}
              />
            </Descriptions.Item>
            <Descriptions.Item label={R.text.last_ride}>
              <FilterInput
                value={lastRideDateInput}
                onChange={handleLastRideDateInput}
                placeholder={R.text.last_ride}
              />
            </Descriptions.Item>
            {auth.band.id === MAIN_BAND_ID && <Descriptions.Item label={"재팬기기"}>
              <FilterSelect
                value={fromJapanSelect}
                onChange={handleFromJapanSelect}
                placeholder="O/X"
                options={binaryOptions}
              />
            </Descriptions.Item>}
            <Descriptions.Item label={"작업상태"}>
              <FilterSelect
                value={vehicleTaskTypeSelect}
                onChange={handleVehicleTaskTypeSelect}
                placeholder="배터리교체하기..."
                options={vehicleTaskTypeOptions}
                mode="multiple"
              />
            </Descriptions.Item>
          </Descriptions>
        </Card>
  );
};

export default ScootsTableFilter;
